import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';

const BUCKET = 'matter-documents';

const MatterDocumentsPanel = ({ matter, mode = 'client', refreshKey = 0 }) => {
  const { profile } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [clientVisible, setClientVisible] = useState(true);
  const [error, setError] = useState(null);

  const canUpload = mode === 'advisor' && (profile?.role === 'advisor_admin' || profile?.role === 'advisor_user');

  const fetchDocuments = async () => {
    const firmId = profile?.advisor_firm_id || matter?.advisor_firm_id;
    if (!matter?.id || !firmId) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchErr } = await supabase
        .from('matter_documents')
        .select('*')
        .eq('matter_id', matter.id)
        .eq('advisor_firm_id', firmId)
        .order('created_at', { ascending: false });

      if (fetchErr) throw fetchErr;
      setDocuments(data || []);
    } catch (err) {
      console.error('Document fetch error:', err);
      setError(err.message || 'Unable to load documents.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [matter?.id, matter?.advisor_firm_id, profile?.advisor_firm_id, refreshKey]);

  const sanitizeFileName = (name) => {
    return name.replace(/[^a-zA-Z0-9._-]/g, '_');
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile || !matter || !profile?.advisor_firm_id) return;

    try {
      setUploading(true);
      setError(null);

      const safeName = sanitizeFileName(selectedFile.name);
      const uniqueName = `${Date.now()}-${crypto.randomUUID()}-${safeName}`;
      const storagePath = `${profile.advisor_firm_id}/${matter.client_id}/${matter.id}/${uniqueName}`;

      const { error: uploadErr } = await supabase.storage
        .from(BUCKET)
        .upload(storagePath, selectedFile, {
          cacheControl: '3600',
          contentType: selectedFile.type || 'application/octet-stream',
          upsert: false
        });

      if (uploadErr) throw uploadErr;

      const { error: insertErr } = await supabase
        .from('matter_documents')
        .insert({
          advisor_firm_id: profile.advisor_firm_id,
          client_id: matter.client_id,
          matter_id: matter.id,
          uploaded_by: profile.id,
          storage_bucket: BUCKET,
          storage_path: storagePath,
          file_name: selectedFile.name,
          content_type: selectedFile.type || null,
          file_size: selectedFile.size,
          visibility: clientVisible ? 'client_visible' : 'internal',
          status: 'active'
        });

      if (insertErr) {
        await supabase.storage.from(BUCKET).remove([storagePath]);
        throw insertErr;
      }

      setSelectedFile(null);
      setClientVisible(true);
      await fetchDocuments();
    } catch (err) {
      console.error('Document upload error:', err);
      setError(err.message || 'Unable to upload document.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (document) => {
    try {
      setError(null);

      const { data, error: signedUrlErr } = await supabase.storage
        .from(document.storage_bucket || BUCKET)
        .createSignedUrl(document.storage_path, 60);

      if (signedUrlErr) throw signedUrlErr;
      window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Document download error:', err);
      setError(err.message || 'Unable to open document.');
    }
  };

  const handleRevoke = async (document) => {
    if (!window.confirm(`Revoke access to "${document.file_name}"? The file will be hidden but retained for audit history.`)) {
      return;
    }

    try {
      setError(null);

      const { error: updateErr } = await supabase
        .from('matter_documents')
        .update({
          status: 'revoked',
          visibility: 'internal'
        })
        .eq('id', document.id)
        .eq('advisor_firm_id', profile.advisor_firm_id);

      if (updateErr) throw updateErr;
      await fetchDocuments();
    } catch (err) {
      console.error('Document revoke error:', err);
      setError(err.message || 'Unable to revoke document.');
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
        <h3 className="font-bold text-slate-800 flex items-center text-sm uppercase tracking-tight">
          <SafeIcon icon={FiIcons.FiFileText} className="mr-2 h-4 w-4 text-slate-400" />
          Documents
        </h3>
        {mode === 'client' && (
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            Approved Files
          </span>
        )}
      </div>

      {canUpload && (
        <form onSubmit={handleUpload} className="p-6 border-b border-slate-100 bg-slate-50/40 space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
              Upload Document
            </label>
            <input
              type="file"
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
            />
          </div>
          <label className="flex items-center text-sm text-slate-600">
            <input
              type="checkbox"
              checked={clientVisible}
              onChange={(e) => setClientVisible(e.target.checked)}
              className="mr-2 rounded border-slate-300 text-slate-800 focus:ring-slate-500"
            />
            Make visible in client portal
          </label>
          <button
            type="submit"
            disabled={!selectedFile || uploading}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900 disabled:opacity-50"
          >
            {uploading ? (
              <>
                <SafeIcon icon={FiIcons.FiLoader} className="animate-spin mr-2 h-4 w-4" />
                Uploading...
              </>
            ) : (
              <>
                <SafeIcon icon={FiIcons.FiUpload} className="mr-2 h-4 w-4" />
                Upload Document
              </>
            )}
          </button>
        </form>
      )}

      {error && (
        <div className="m-6 bg-red-50 border-l-4 border-red-400 p-4 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-slate-400">
          <SafeIcon icon={FiIcons.FiLoader} className="animate-spin h-6 w-6 mx-auto mb-2" />
          <p className="text-sm">Loading documents...</p>
        </div>
      ) : documents.length > 0 ? (
        <div className="divide-y divide-slate-100">
          {documents.map((document) => (
            <div key={document.id} className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold text-slate-900 truncate">{document.file_name}</p>
                  {mode === 'advisor' && document.status !== 'active' && (
                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-full bg-red-50 text-red-700 border border-red-100">
                      {document.status}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Added {format(new Date(document.created_at), 'MMM d, yyyy')}
                  {mode === 'advisor' && ` - ${document.visibility === 'client_visible' ? 'Client visible' : 'Internal only'}`}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => handleDownload(document)}
                  className="inline-flex items-center justify-center px-3 py-2 text-xs font-bold uppercase tracking-widest rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                >
                  <SafeIcon icon={FiIcons.FiDownload} className="mr-2 h-4 w-4" />
                  Download
                </button>
                {canUpload && document.status === 'active' && (
                  <button
                    onClick={() => handleRevoke(document)}
                    className="inline-flex items-center justify-center px-3 py-2 text-xs font-bold uppercase tracking-widest rounded-md bg-red-50 text-red-700 border border-red-100 hover:bg-red-100 transition-colors"
                  >
                    <SafeIcon icon={FiIcons.FiXCircle} className="mr-2 h-4 w-4" />
                    Revoke
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-8 text-center text-slate-500">
          <SafeIcon icon={FiIcons.FiFileText} className="h-10 w-10 text-slate-200 mx-auto mb-3" />
          <p className="text-sm">
            {mode === 'advisor'
              ? 'No documents have been uploaded for this plan yet.'
              : 'No approved documents are available yet.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default MatterDocumentsPanel;
